/**
 * Followers, standing total (R5, plan §10a.1) — the level the packs quote
 * (1,003 on 27 Aug, 1,251 on 15 Sep in the manual history).
 *
 * LinkedIn is collected daily from the Community Management API. X is TYPED
 * IN: verified 2026-09-23, Buffer's API exposes no follower or audience field
 * (its Channel type has none, only post metrics) and we hold no X key — so
 * the tile shows when the X figure was entered and by which pull, and never
 * pretends it was collected.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMetricSeries, recordManualMetric } from '../../lib/api/analytics.js';
import { SITE_LABEL, SITE_ORDER, addDays, toNumber } from '../../lib/analyticsShape.js';
import { VisibilityTag } from './StatTile.js';

export interface LatestBySite { site: string; value: number; day: string }

/** The most recent value per site within the fetched window. */
export function latestPerSite(rows: Array<{ site: string; metric_date: string; value: string | number }>): LatestBySite[] {
  const best = new Map<string, LatestBySite>();
  for (const r of rows) {
    const v = toNumber(r.value);
    if (v == null) continue;
    const prev = best.get(r.site);
    if (!prev || r.metric_date > prev.day) best.set(r.site, { site: r.site, value: v, day: r.metric_date });
  }
  return Array.from(best.values());
}

function fmtDay(day: string): string {
  return new Date(`${day}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/** One masthead's X count: today's entry, with the last stored figure beside it. */
function XEntryRow({ site, last }: { site: string; last: LatestBySite | undefined }) {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const save = useMutation({
    mutationFn: () => recordManualMetric({ metricKey: 'social.followers_x', site, value: Number(value) }),
    onSuccess: () => { setValue(''); qc.invalidateQueries({ queryKey: ['standing-x'] }); },
  });
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => { e.preventDefault(); if (value && !save.isPending) save.mutate(); }}
    >
      <span className="w-32 text-xs text-grey-700">{SITE_LABEL[site] ?? site}</span>
      <input
        value={value}
        onChange={(e) => { setValue(e.target.value.replace(/[^\d]/g, '')); save.reset(); }}
        inputMode="numeric"
        placeholder="Today's count"
        aria-label={`${SITE_LABEL[site] ?? site} X followers today`}
        className="w-28 rounded-full border border-grey-200 bg-surface px-3 py-1 text-xs text-grey-900 tabular-nums"
      />
      <button
        type="submit"
        disabled={!value || save.isPending}
        className="rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
      <span className="text-xs text-grey-500 tabular-nums">
        {last ? `Last ${last.value.toLocaleString()} · ${fmtDay(last.day)}` : 'Not entered yet'}
      </span>
      {save.isSuccess && <span className="text-xs text-primary-700">Saved</span>}
      {save.isError && <span className="text-xs text-danger-600">{(save.error as Error).message}</span>}
    </form>
  );
}

export function StandingFollowers({ to }: { to: string }) {
  // The page's range ends yesterday (the sweep collects complete days), but a
  // typed X count is stored under today — read the level through tomorrow so
  // an entry shows the moment it is saved, whatever the UTC/local offset.
  const through = addDays(to, 2);
  const from = addDays(to, -45);
  const li = useQuery({
    queryKey: ['standing-li', from, through],
    queryFn: () => getMetricSeries({ metric: 'li.followers.total', dateFrom: from, dateTo: through }),
    staleTime: 60_000,
  });
  const x = useQuery({
    queryKey: ['standing-x', from, through],
    queryFn: () => getMetricSeries({ metric: 'social.followers_x', dateFrom: from, dateTo: through }),
    staleTime: 60_000,
  });

  const liRows = useMemo(() => latestPerSite(li.data?.rows ?? []), [li.data]);
  const xRows = useMemo(() => latestPerSite(x.data?.rows ?? []), [x.data]);
  const liTotal = liRows.reduce((a, b) => a + b.value, 0);
  const xTotal = xRows.reduce((a, b) => a + b.value, 0);
  const xAsAt = xRows.map((r) => r.day).sort().pop() ?? null;
  const xSites = SITE_ORDER.filter((s) => s !== 'assayernetwork');

  return (
    <section className="bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-2" aria-label="Followers standing">
      <header className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-grey-900">Followers · standing total</h3>
          <p className="text-xs text-grey-500 mt-0.5">A level, never summed across periods — distinct from new followers, which is a flow.</p>
        </div>
        <VisibilityTag visibility="public_safe" />
      </header>
      <p className="text-2xl font-bold text-grey-900 tabular-nums">
        {(liTotal + xTotal).toLocaleString()}
      </p>
      <p className="text-xs text-grey-600">
        LinkedIn {liTotal.toLocaleString()} (collected)
        {' · '}X {xTotal ? xTotal.toLocaleString() : '—'} {xAsAt ? `(entered ${xAsAt})` : '(not entered)'}
      </p>
      <p className="text-xs text-grey-500">
        {liRows.sort((a, b) => SITE_ORDER.indexOf(a.site as never) - SITE_ORDER.indexOf(b.site as never))
          .map((r) => `${SITE_LABEL[r.site] ?? r.site} ${r.value.toLocaleString()}`).join(' · ')}
      </p>
      <div className="flex flex-col gap-1.5 pt-1" aria-label="X followers entry">
        <span className="text-xs font-medium text-grey-700">X followers · enter today's count</span>
        {xSites.map((s) => <XEntryRow key={s} site={s} last={xRows.find((r) => r.site === s)} />)}
      </div>
      <footer className="text-[11px] text-grey-400">
        LinkedIn · Community Management API, daily · X · typed in: Buffer's API exposes no follower field and we hold no X key
      </footer>
    </section>
  );
}
